import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ─── Query Keys ──────────────────────────────────────────
export const queryKeys = {
  companies: ["companies"] as const,
  company: (id: string) => ["company", id] as const,
  kpis: (companyId: string) => ["kpis", companyId] as const,
  kpiValues: (kpiId: string) => ["kpi-values", kpiId] as const,
  kpiValuesForWeek: (companyId: string, week: string) => ["kpi-values-week", companyId, week] as const,
  portfolioStatus: ["portfolio-status"] as const,
  profile: (userId: string) => ["profile", userId] as const,
  pendingKPIs: (companyId: string) => ["pending-kpis", companyId] as const,
  activityFeed: (companyId: string) => ["activity-feed", companyId] as const,
  reports: (companyId: string) => ["reports", companyId] as const,
};

// ─── Fetch helpers ───────────────────────────────────────
export function useCompanies() {
  return useQuery({
    queryKey: queryKeys.companies,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useCompany(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.company(id!),
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

export function useKPIs(companyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.kpis(companyId!),
    enabled: !!companyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kpis")
        .select("*")
        .eq("company_id", companyId!)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });
}

export function useKPIValues(kpiId: string | undefined, limit = 12) {
  return useQuery({
    queryKey: queryKeys.kpiValues(kpiId!),
    enabled: !!kpiId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kpi_values")
        .select("*")
        .eq("kpi_id", kpiId!)
        .order("week_start_date", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data;
    },
  });
}

// ─── Portfolio (single aggregated query) ─────────────────
export function usePortfolioStatus() {
  return useQuery({
    queryKey: queryKeys.portfolioStatus,
    queryFn: async () => {
      // Fetch all companies, KPIs, and latest 2 values per KPI in minimal queries
      const { data: companies, error: cErr } = await supabase
        .from("companies")
        .select("id, name");
      if (cErr) throw cErr;

      if (!companies || companies.length === 0) return [];

      const companyIds = companies.map((c) => c.id);
      const { data: allKpis, error: kErr } = await supabase
        .from("kpis")
        .select("id, company_id, min_band, max_band")
        .in("company_id", companyIds);
      if (kErr) throw kErr;

      const kpiIds = (allKpis || []).map((k) => k.id);
      let allValues: { kpi_id: string; value: number; week_start_date: string }[] = [];
      if (kpiIds.length > 0) {
        const { data: vals, error: vErr } = await supabase
          .from("kpi_values")
          .select("kpi_id, value, week_start_date")
          .in("kpi_id", kpiIds)
          .order("week_start_date", { ascending: false });
        if (vErr) throw vErr;
        allValues = vals || [];
      }

      // Group values by kpi_id (already sorted desc)
      const valuesByKpi: Record<string, typeof allValues> = {};
      for (const v of allValues) {
        if (!valuesByKpi[v.kpi_id]) valuesByKpi[v.kpi_id] = [];
        if (valuesByKpi[v.kpi_id].length < 2) {
          valuesByKpi[v.kpi_id].push(v);
        }
      }

      return companies.map((company) => {
        const companyKpis = (allKpis || []).filter((k) => k.company_id === company.id);
        let red = 0, amber = 0, green = 0;

        for (const kpi of companyKpis) {
          const vals = valuesByKpi[kpi.id] || [];
          const latest = vals[0];
          const prev = vals[1];
          if (!latest) continue;

          let status = "green";
          if (kpi.min_band && latest.value < kpi.min_band) status = "red";
          if (kpi.max_band && latest.value > kpi.max_band) status = "red";
          if (status === "green" && prev) {
            const pct = prev.value !== 0 ? (Math.abs(latest.value - prev.value) / prev.value) * 100 : 0;
            if (pct > 20) status = "amber";
          }
          if (status === "red") red++;
          else if (status === "amber") amber++;
          else green++;
        }

        let trend: "up" | "down" | "stable" = "stable";
        if (red > 0) trend = "down";
        else if (green > companyKpis.length * 0.8) trend = "up";

        return { id: company.id, name: company.name, red, amber, green, total: companyKpis.length, trend };
      });
    },
  });
}

// ─── Pending KPIs for current week (for nudges) ──────────
export function usePendingKPIs(companyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.pendingKPIs(companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
    queryFn: async () => {
      const now = new Date();
      const day = now.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      const monday = new Date(now);
      monday.setDate(now.getDate() + diff);
      const weekStart = monday.toISOString().split("T")[0];

      const { data: kpis, error: kErr } = await supabase
        .from("kpis")
        .select("id, name")
        .eq("company_id", companyId!);
      if (kErr) throw kErr;
      if (!kpis || kpis.length === 0) return { pending: 0, total: 0, names: [] as string[] };

      const kpiIds = kpis.map((k) => k.id);
      const { data: submitted, error: vErr } = await supabase
        .from("kpi_values")
        .select("kpi_id")
        .in("kpi_id", kpiIds)
        .eq("week_start_date", weekStart);
      if (vErr) throw vErr;

      const submittedSet = new Set((submitted || []).map((v) => v.kpi_id));
      const pendingKPIs = kpis.filter((k) => !submittedSet.has(k.id));
      return { pending: pendingKPIs.length, total: kpis.length, names: pendingKPIs.map((k) => k.name) };
    },
  });
}

// ─── Activity Feed ───────────────────────────────────────
export function useActivityFeed(companyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.activityFeed(companyId!),
    enabled: !!companyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("audit_logs")
        .select("id, entity, action, entity_id, actor_user_id, created_at")
        .eq("company_id", companyId!)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data || [];
    },
  });
}

// ─── Reports ─────────────────────────────────────────────
export function useReports(companyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.reports(companyId!),
    enabled: !!companyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reports")
        .select("*")
        .eq("company_id", companyId!)
        .order("week_start_date", { ascending: false })
        .limit(12);
      if (error) throw error;
      return data || [];
    },
  });
}

// ─── Submit KPI Value mutation ───────────────────────────
export function useSubmitKPIValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      kpiId: string;
      value: number;
      weekStart: string;
      note?: string;
      evidenceUrl?: string;
      userId: string;
    }) => {
      const { error } = await supabase.from("kpi_values").insert({
        kpi_id: params.kpiId,
        week_start_date: params.weekStart,
        value: params.value,
        note: params.note || null,
        evidence_url: params.evidenceUrl || null,
        submitted_by: params.userId,
        late: false,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kpi-values"] });
      qc.invalidateQueries({ queryKey: ["pending-kpis"] });
      qc.invalidateQueries({ queryKey: queryKeys.portfolioStatus });
    },
  });
}

// ─── User Profile ────────────────────────────────────────
export function useProfile() {
  return useQuery({
    queryKey: ["current-profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return { ...data, email: user.email };
    },
  });
}
